import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from '../../shared/services/user/user.service';
import { User } from '../../shared/services/user/user';
import { AuthService } from '../../shared/auth/auth.service';
import { TrackerService } from 'src/app/shared/util/tracker.service';
import { environment } from 'src/environments/environment';
import { InformationComponent, ErrorComponent } from 'src/app/shared/util/helpers';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  user: User | null = null;
  private userSnapshot: User | null = null;

  isLoading = true;
  isSaving = false;
  error: string | null = null;
  successMessage: string | null = null;

  readonly assistantHref = environment.assistantApp;

  constructor(
    private userService: UsersService,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private trackerService: TrackerService
  ) {}

  ngOnInit(): void {
    this.trackerService.trackPageViews();
    this.loadUserProfile();
  }

  get displayName(): string {
    if (!this.user) {
      return '';
    }
    const parts = [this.user.firstName, this.user.lastName].filter(Boolean);
    if (parts.length) {
      return parts.join(' ');
    }
    return this.user.username || this.user.email || '';
  }

  get userInitials(): string {
    if (!this.user) {
      return '?';
    }
    const fromName = [this.user.firstName, this.user.lastName]
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase())
      .join('');
    if (fromName) {
      return fromName.slice(0, 2);
    }
    const username = this.user.username || this.user.email || '?';
    return username.slice(0, 2).toUpperCase();
  }

  loadUserProfile(): void {
    this.isLoading = true;
    this.error = null;

    this.userService.getCurrentUser().subscribe({
      next: (userData) => {
        this.user = { ...userData };
        this.userSnapshot = { ...userData };
        this.isLoading = false;
      },
      error: (err) => {
        if (err?.status === 401) {
          this.error = 'PROFILE.ERROR_UNAUTHORIZED';
          this.authService.logout();
          this.router.navigate(['/login']);
        } else if (err?.status === 404) {
          this.error = 'PROFILE.ERROR_NOT_FOUND';
        } else if (err?.status === 0) {
          this.error = 'PROFILE.ERROR_NETWORK';
        } else if (typeof err === 'string') {
          this.error = err;
        } else {
          this.error = 'PROFILE.ERROR_LOADING';
        }
        this.isLoading = false;
      },
    });
  }

  resetForm(): void {
    if (this.userSnapshot) {
      this.user = { ...this.userSnapshot };
      this.successMessage = null;
      this.error = null;
    }
  }

  updateProfile(): void {
    if (!this.user || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.error = null;
    this.successMessage = null;

    this.userService.updateProfile(this.user).subscribe({
      next: (updatedUser) => {
        this.user = { ...updatedUser };
        this.userSnapshot = { ...updatedUser };
        this.isSaving = false;
        this.successMessage = 'PROFILE.MESSAGES.UPDATE_SUCCESS';
        this.snackBar.openFromComponent(InformationComponent, {
          data: { message: 'PROFILE.MESSAGES.UPDATE_SUCCESS' },
          duration: 3000,
        });
      },
      error: () => {
        this.error = 'PROFILE.ERROR_UPDATING';
        this.isSaving = false;
        this.snackBar.openFromComponent(ErrorComponent, {
          data: { message: 'PROFILE.ERROR_UPDATING' },
          duration: 3000,
        });
      },
    });
  }
}
